/**
 * ToolkitFAB — Centered-bottom floating action button that combines
 * the existing Demo Toolkit + Page Guide entry points into one
 * non-intrusive control.
 *
 * Why centered-bottom? Right-anchored floaters were colliding with
 * Next/Back navigation rows on wizard-style pages. Centering them
 * keeps them visually present without shadowing nav controls.
 *
 * Behavior:
 *   - Collapsed state: a single dark pill labelled "Tools" with a
 *     wand icon. Subtle, matches the slate-900 aesthetic of the
 *     existing toolkit pill so SEs recognise it.
 *   - Click → expands to a card containing two child pills:
 *       1. "Demo Toolkit"     → fires `lie:toggleDemoToolkit` event
 *       2. "Page Guide"       → fires `lie:openGlossary` event
 *     The existing DemoToolkit + GlossaryMode components listen for
 *     these events (see their own files) so this component does not
 *     need refs into them.
 *   - Click outside (or Esc) → collapse.
 *   - Hidden on /login, /register, /onboarding, /demo-preview, share
 *     routes — anywhere the Layout wrapper isn't rendered, this
 *     component isn't rendered either, so nothing to do here.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Wand2, FlaskConical, BookOpen, ChevronUp, X } from 'lucide-react';
import { cn } from '../../lib/utils';
import DemoWarmupButton from '../demo/DemoWarmupButton';
import AutoWarmupControl from '../demo/AutoWarmupControl';
import WarmupReport from '../demo/WarmupReport';
import { useDemoReady, useDemoWarmup, agoLabel } from '../../hooks/useDemoWarmup';

export function ToolkitFAB() {
  const [open, setOpen] = useState(false);
  const [scrolling, setScrolling] = useState(false);
  const wrapRef = useRef(null);

  // Close when clicking outside or pressing Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Scroll-aware auto-fade — while the user is actively scrolling, dim the
  // FAB so it recedes visually on long benchmarking pages; restore full
  // opacity after 250 ms of idle. Skipped when the panel is open so the
  // user can actually interact with it mid-scroll.
  useEffect(() => {
    if (open) {
      setScrolling(false);
      return undefined;
    }
    let timeout = null;
    const onScroll = () => {
      setScrolling(true);
      if (timeout) clearTimeout(timeout);
      timeout = setTimeout(() => setScrolling(false), 250);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (timeout) clearTimeout(timeout);
    };
  }, [open]);

  const fire = (eventName) => {
    window.dispatchEvent(new CustomEvent(eventName));
    setOpen(false);
  };

  return (
    <div
      ref={wrapRef}
      className={cn(
        'fixed bottom-5 left-1/2 -translate-x-1/2 z-50 flex flex-col items-center gap-2',
        'transition-opacity duration-200 ease-out',
        scrolling ? 'opacity-40 hover:opacity-100' : 'opacity-100',
      )}
      data-testid="toolkit-fab"
    >
      {open && (
        <div
          className="flex flex-col sm:flex-row gap-2 bg-white rounded-2xl shadow-2xl border border-slate-200 p-2 origin-bottom animate-in fade-in slide-in-from-bottom-2 duration-150"
          data-testid="toolkit-fab-panel"
        >
          <DemoWarmupButton variant="tile" />
          <WarmupReport />
          <AutoWarmupControl />
          <button
            type="button"
            onClick={() => fire('lie:toggleDemoToolkit')}
            className="flex items-center gap-2.5 px-4 py-2.5 hover:bg-indigo-50 rounded-xl transition-colors text-left whitespace-nowrap group"
            data-testid="toolkit-fab-demo-btn"
          >
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-indigo-500 to-violet-600 text-white">
              <FlaskConical className="h-4 w-4" />
            </span>
            <span className="flex flex-col">
              <span className="text-sm font-semibold text-slate-900 group-hover:text-indigo-700">
                Demo Toolkit
              </span>
              <span className="text-[11px] text-slate-500">
                Tour, Director, recordings
              </span>
            </span>
          </button>
          <button
            type="button"
            onClick={() => fire('glossary:open')}
            className="flex items-center gap-2.5 px-4 py-2.5 hover:bg-amber-50 rounded-xl transition-colors text-left whitespace-nowrap group"
            data-testid="toolkit-fab-guide-btn"
          >
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 text-white">
              <BookOpen className="h-4 w-4" />
            </span>
            <span className="flex flex-col">
              <span className="text-sm font-semibold text-slate-900 group-hover:text-amber-700">
                Page Guide
              </span>
              <span className="text-[11px] text-slate-500">
                Press <kbd className="font-mono">?</kbd> · explanations
              </span>
            </span>
          </button>
        </div>
      )}

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? 'Close tools' : 'Open tools'}
        className={cn(
          'flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white shadow-xl border border-white/10 transition-all',
          'hover:scale-105 active:scale-95',
          open
            ? 'bg-slate-700'
            : 'bg-gradient-to-r from-slate-900 to-slate-800 hover:from-slate-800 hover:to-slate-700',
        )}
        data-testid="toolkit-fab-trigger"
      >
        {open ? (
          <X className="h-4 w-4" />
        ) : (
          <Wand2 className="h-4 w-4 text-amber-300" />
        )}
        <span>{open ? 'Close' : 'Tools'}</span>
        {!open && <DemoReadyBadge />}
        {!open && <ChevronUp className="h-3.5 w-3.5 opacity-70" />}
      </button>
    </div>
  );
}

// "Demo ready · cached 12 min ago" — only while the Prepare-demo copies are live (wiped by Resync / 24h).
function DemoReadyBadge() {
  const ready = useDemoReady();
  const { running } = useDemoWarmup();
  if (running) {
    return (
      <span className="ml-1 inline-flex items-center gap-1.5 rounded-full bg-white/10 px-2 py-0.5 text-[11px] font-medium text-amber-200" data-testid="toolkit-demo-ready-badge" data-state="preparing">
        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-amber-300" /> Preparing…
      </span>
    );
  }
  if (!ready) return null;
  const warn = ready.failed > 0;
  return (
    <span
      className={cn('ml-1 inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium', warn ? 'bg-amber-400/15 text-amber-200' : 'bg-emerald-400/15 text-emerald-200')}
      title={`${ready.views} views cached${warn ? ` · ${ready.failed} page(s) incomplete` : ''}`}
      data-testid="toolkit-demo-ready-badge"
      data-state={warn ? 'partial' : 'ready'}
    >
      <span className={cn('h-1.5 w-1.5 rounded-full', warn ? 'bg-amber-300' : 'bg-emerald-300')} />
      {warn ? 'Demo partly ready' : 'Demo ready'} · cached {agoLabel(ready.at)}
    </span>
  );
}

export default ToolkitFAB;
